/**
 * SSO Helper functions for cross-application authentication
 */

/**
 * Generate SSO URL for redirecting to the merchant dashboard
 * @param {string} token - Access token from the frontend app
 * @param {any} user - User object from the frontend app
 * @param {string} redirectPath - Optional path to redirect to after login (default: '/')
 * @returns {string} Full URL to redirect to
 */
export const generateDashboardSSOUrl = (
  token: string,
  user: any,
  redirectPath: string = "/"
): string => {
  const dashboardBaseUrl =
    process.env.REACT_APP_DASHBOARD_URL || "http://localhost:3001";

  // Prepare user data (only essential fields to keep URL manageable)
  const userData = {
    id: user?.id,
    email: user?.email,
    first_name: user?.first_name,
    last_name: user?.last_name,
    role_id: user?.role_id,
  };

  // Encode user data
  const encodedUser = encodeURIComponent(JSON.stringify(userData));

  // Build SSO URL with query parameters
  const ssoUrl = new URL(`${dashboardBaseUrl}/auth/sso-login`);
  ssoUrl.searchParams.set("token", token);
  ssoUrl.searchParams.set("user", encodedUser);

  if (redirectPath && redirectPath !== "/") {
    ssoUrl.searchParams.set("redirect", redirectPath);
  }

  return ssoUrl.toString();
};

/**
 * Check if user is a merchant (has role_id 1 or 3)
 * @param {any} user - User object
 * @returns {boolean}
 */
export const isMerchantUser = (user: any): boolean => {
  return user?.role_id === 3;
};

/**
 * Redirect to merchant dashboard with SSO
 * @param {string} token - Access token
 * @param {any} user - User object
 * @param {string} redirectPath - Optional path to redirect to after login
 */
export const redirectToDashboard = (
  token: string,
  user: any,
  redirectPath: string = "/"
): boolean => {
  if (!isMerchantUser(user)) {
    console.warn("User is not a merchant. Cannot redirect to dashboard.");
    return false;
  }

  const ssoUrl = generateDashboardSSOUrl(token, user, redirectPath);
  window.location.href = ssoUrl;
  return true;
};

/**
 * Open merchant dashboard in new tab with SSO
 * @param {string} token - Access token
 * @param {any} user - User object
 * @param {string} redirectPath - Optional path to redirect to after login
 */
export const openDashboardInNewTab = (
  token: string,
  user: any,
  redirectPath: string = "/"
): boolean => {
  if (!isMerchantUser(user)) {
    console.warn("User is not a merchant. Cannot open dashboard.");
    return false;
  }

  const ssoUrl = generateDashboardSSOUrl(token, user, redirectPath);
  window.open(ssoUrl, "_blank");
  return true;
};

